import { Injectable } from '@angular/core';
import { WorkspaceSession } from '@common/sockets/workspace-session';
import { WorkspacePullFileState } from '@common/workspace/models/workspace-pull-file-outcome.model';
import { WorkspacePullStateGridItem } from '@common/workspace/models/workspace-pull-state-grid-item.model';
import { WorkspacePullState } from '@common/workspace/models/workspace-pull-state.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { CollabPullsDataService } from '@portal-core/collab/services/collab-pulls/collab-pulls-data.service';
import { OnlineService } from '@portal-core/general/services/online.service';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * Per-dialog subscriber that maintains a live `pullState$` for one workspace by opening a
 * dedicated socket to collab-workspace and folding the same `workspace.pull.*` events that
 * drive the workspace-page pull dialog. Used by the portal-admin pulls grid where no
 * `WorkspaceSessionService` is bound to the clicked workspace — the dialog needs its own
 * scoped event stream.
 *
 * Idle until {@link start} is called, so providing this `providedIn: 'root'` is free in
 * portal-client where nothing ever starts it. Single-instance is fine because at most one
 * admin pull dialog is open at a time; if a new {@link start} arrives mid-session the
 * previous socket is torn down first.
 */
@Injectable({ providedIn: 'root' })
export class AdminWorkspacePullSubscriberService {
  private workspaceSession: WorkspaceSession | null = null;
  private onlineSubscription: Subscription | null = null;
  private connectedSubscription: Subscription | null = null;

  /**
   * Mirror of the running/terminal pull state for the active subscription, identical in shape
   * to {@link WorkspacePullProgressService.pullState$}. Reset to null on every {@link start}
   * so the dialog never briefly shows stale data from the previous workspace.
   */
  pullState$: BehaviorSubject<WorkspacePullState | null> = new BehaviorSubject<WorkspacePullState | null>(null);

  /**
   * True when the bootstrap `GET /pull/state` returned no record after connect — i.e. Redis's
   * terminal-state grace TTL aged out before the user opened the dialog. The grid row's seed
   * is therefore stale (typically still says `running`) and we can no longer recover the real
   * outcome. The dialog uses this to render an "outcome no longer tracked" message instead of
   * leaving the user staring at a spinner that will never finish.
   */
  stateExpired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private collabApiService: CollabApiService,
    private collabPullsDataService: CollabPullsDataService,
    private onlineService: OnlineService
  ) { }

  /**
   * Pushes the latest state into the `CollabPulls` NGXS store so the admin grid row reflects
   * the live progress/outcome without waiting for a grid refresh. Mirrors `workspaceId` into
   * `Id` because `CollectionDataServiceBase` keys items by `Id`.
   */
  private mirrorToDataStore(state: WorkspacePullState) {
    const item: WorkspacePullStateGridItem = { ...state, Id: state.workspaceId };
    this.collabPullsDataService.updateItems$([item]).subscribe();
  }

  /**
   * Opens a socket to collab-workspace scoped to `projectId`/`branchName` and starts mirroring
   * pull events into {@link pullState$}. On connect, bootstraps via `GET /pull/state` so a
   * dialog opened on an in-flight pull doesn't blank-render until the next progress event.
   * Safe to call repeatedly; the previous session is torn down first.
   */
  start(projectId: number, branchName: string, initialState: WorkspacePullState | null = null) {
    this.stop();
    // Seed with the caller's snapshot (typically the grid row) so the dialog renders immediately
    // instead of blank-flashing for the ~1s it takes the socket to connect and the bootstrap
    // GET to return. Socket events and the bootstrap response will overwrite this as fresher
    // data arrives.
    this.pullState$.next(initialState);
    this.stateExpired$.next(false);

    this.workspaceSession = new WorkspaceSession({
      socketBaseUrl: this.apiService.collabWorkspaceUrl,
      offline: !this.onlineService.online,
      refreshToken$: () => this.authService.refreshToken$(),
      branchName,
      projectId
    });

    this.onlineSubscription = this.onlineService.online$.subscribe(online => {
      if (this.workspaceSession) {
        this.workspaceSession.offline = !online;
      }
    });

    this.connectedSubscription = this.workspaceSession.connected$.subscribe(connected => {
      if (!connected) return;
      // Recover the current state from Redis. If a pull is in flight we'll see `running`; if it
      // just finished we'll see the terminal record (kept in Redis for a short grace TTL).
      // Overwrites any seed state from `start()` with the fresher server-side snapshot.
      this.collabApiService.getWorkspacePullState$(projectId, branchName).subscribe({
        next: response => {
          const state = response?.Result?.pullState ?? null;
          if (state) {
            this.pullState$.next(state);
            this.mirrorToDataStore(state);
          } else {
            // No record server-side. The seed (if any) is therefore the last anyone knows about
            // this pull. Flag it so the dialog can stop pretending the pull is still in flight.
            this.stateExpired$.next(true);
          }
        },
        error: () => {
          // Swallow — socket events will catch us up on the next progress tick.
        }
      });
    });

    const onState = (state: WorkspacePullState) => {
      this.pullState$.next(state);
      this.mirrorToDataStore(state);
    };
    this.workspaceSession.on('workspace.pull.started', onState);
    this.workspaceSession.on('workspace.pull.progress', onState);
    this.workspaceSession.on('workspace.pull.completed', onState);
    this.workspaceSession.on('workspace.pull.failed', onState);

    // Per-file completion deltas. Fold into the current state's `fileOutcomes` map so the
    // dialog's per-row UI updates without waiting for the next aggregate progress emit.
    this.workspaceSession.on('workspace.pull.file-completed', (data: { filePath: string; fileOutcome: WorkspacePullFileState }) => {
      const current = this.pullState$.value;
      if (!current) return;
      const next: WorkspacePullState = {
        ...current,
        fileOutcomes: { ...current.fileOutcomes, [data.filePath]: data.fileOutcome }
      };
      this.pullState$.next(next);
      this.mirrorToDataStore(next);
    });

    this.workspaceSession.start();
  }

  /**
   * Tears down the socket and clears state. Idempotent.
   */
  stop() {
    this.onlineSubscription?.unsubscribe();
    this.onlineSubscription = null;
    this.connectedSubscription?.unsubscribe();
    this.connectedSubscription = null;

    if (this.workspaceSession) {
      this.workspaceSession.stop();
      this.workspaceSession = null;
    }
    this.pullState$.next(null);
    this.stateExpired$.next(false);
  }
}
